const loadjs = require("loadjs");
const _ = require("lodash");
const queryString = require("query-string");
const URLMake = require("../../../shared/URLMake");
const StoreUtil = require("../../../shared/StoreUtil");
const ErrorUtil = require("../../../shared/ErrorUtil");
const OpsPage = require("../vault/OpsPage");
const OpsUser = require("../vault/OpsUser");
const uuidv4 = require("uuid").v4;

const COOKIE_DURATION = 3650 * 24 * 60 * 60 * 1000; // ~ ten years
const isProduction = process.env.NODE_ENV === "production";
let lastURL = null;
let started = false;
let stopped = false;
let queue = [];
// Flags used to avoid double-firing GA4 events
const GA4Flags = {
	waitingForCartOnCartPage: false,
	checkoutAlreadyBegun: false,
	shippingInfoAlreadyAdded: false,
	paymentInfoAlreadyAdded: false,
};
// Flag used to avoid double-firing tracking events on first page load
let suppressedFirstPageEvents = false;

// Internal helper that computes a URL to send to analytics from a page object.
// The URL should not contain IDs or other things.
const makeAnalyticsURL = function(page) {
	return URLMake.forPageState(page);
};

const replay = () => {
	_.each(queue, ([f, ...args]) => {
		f(...args);
	});
	queue = [];
};

const isActive = function() {
	return !!(global.window && started && !stopped);
};

const start = function(api, cookieUtil, user) {
	if (started || stopped || !global.window) {
		return;
	}

	logMarketingSource(api, cookieUtil);

	// Part of Google global site tag setup
	global.dataLayer = global.dataLayer || [];
	global.pushToDataLayer = function(data) {
		// Updated from using just "arguments" so we can directly add properties.
		// dataLayer.push is overriden by Google code.
		// To fire events, use data format {event: "eventName", eventModel: dataObject}
		// To add properties to the data layer use data format {propertyName: propertyValue}
		global.dataLayer.push(data); // eslint-disable-line prefer-rest-params
	};

	started = true;
	/* eslint-disable */
	if (global.ga) {
		global.ga("require", "maxScrollTracker");
		global.ga("require", "outboundLinkTracker");
		global.ga("require", "ecommerce");
	}

	loadjs(global.getDistURL("autotrack.js"));

	// Don't start Hotjar for school students; remove once school students are trackable
	if (isProduction && !OpsUser.isSchoolStudent(user)) {
		// Starting Hotjar
		(function(h, o, t, j, a, r) {
			h.hj =
				h.hj ||
				function() {
					(h.hj.q = h.hj.q || []).push(arguments);
				};
			h._hjSettings = {
				hjid: 964617,
				hjsv: 6,
			};
			a = o.getElementsByTagName("head")[0];
			r = o.createElement("script");
			r.async = 1;
			r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
			a.appendChild(r);
		})(window, document, "https://static.hotjar.com/c/hotjar-", ".js?sv=");
	}

	/* eslint-enable */
	try {
		replay();
	} catch (error) {
		ErrorUtil.log("E_ANALYTICS", error);
	}
};

const stop = function() {
	if (stopped || !global.window) {
		return;
	}
	stopped = true;
	if (global.pushToDataLayer) {
		global.pushToDataLayer({stopTracking: true});
	}
};

const logMarketingSource = function(api, cookieUtil) {
	const search = _.get(window, "location.search");
	const searchAsObject = queryString.parse(search);
	const utmParams = {
		utm_source: "source",
		utm_medium: "medium",
		utm_campaign: "campaign",
		utm_content: "content",
		utm_term: "term",
	};
	const clientDeviceID = getClientDeviceID(cookieUtil);
	// Only log if one of the query string parameters is present
	if (_.intersection(_.keys(searchAsObject), _.keys(utmParams)).length) {
		const newParams = {};
		_.each(utmParams, (abbreviation, paramName) => {
			let qsValue = searchAsObject[paramName] || "";
			if (_.isArray(searchAsObject[paramName])) {
				qsValue = qsValue[0];
			}
			newParams[abbreviation] = String(qsValue).replace(/\W/g, "") || null;
		});
		api.logMarketingSource(newParams, clientDeviceID);
	}
};

const conversionWrapper = f => {
	return (...args) => {
		if (!started && !stopped) {
			queue.push([f, ...args]);
		} else if (!stopped) {
			try {
				f(...args);
			} catch (error) {
				ErrorUtil.log("E_ANALYTICS", error);
			}
		}
	};
};

const sendEmailSignup = conversionWrapper(() => {
	// Facebook Pixel and Google Ads conversion
	if (global.dataLayer) {
		global.dataLayer.push({event: "joinMailingList"});
	}
});

const sendOrder = conversionWrapper((orderID, cart, isNewCustomer) => {
	const revenue = Number(cart.prices.total);
	const shipping = Number(cart.prices.shipping);
	const tax = Number(cart.prices.tax);
	const coupon = _.get(cart, "coupon.code", null);
	// Setup GA ECommerce Transaction
	if (global.ga) {
		global.ga("ecommerce:addTransaction", {
			id: orderID,
			revenue,
			shipping,
			tax,
		});
	}

	// Item Setup
	const itemsWithPrices = StoreUtil.calculateItemPrices(cart.items, {
		perItem: true,
	});
	const facebookContents = [];
	itemsWithPrices.forEach(item => {
		const itemPrice = item.price.toFixed(2);
		if (global.ga) {
			global.ga("ecommerce:addItem", {
				id: orderID,
				name: item.sku,
				sku: item.sku,
				quantity: item.quantity,
				category: StoreUtil.getItemType(item),
				price: itemPrice,
			});
		}
		facebookContents.push({
			orderID,
			id: item.sku,
			quantity: item.quantity,
			item_price: itemPrice,
		});
	});
	if (global.ga) {
		global.ga("ecommerce:send");
	}

	// DataLayer purchase event (combines facebook and GA4 data which GTM will parse)
	sendDataLayerEvent("purchase", {
		transaction_id: orderID,
		value: revenue,
		shipping,
		tax,
		currency: "USD",
		items: convertCartItemsToGA4Format(itemsWithPrices),
		coupon,
		contents: facebookContents,
		content_type: "product",
		is_new_customer: isNewCustomer,
		content_ids: itemsWithPrices.map(item => item.sku),
	});
});

const sendPageEvents = conversionWrapper((page, forceURL, cart) => {
	const url = makeAnalyticsURL(page);

	const sendGA4ViewCart = () => {
		const items = [];
		const itemsWithPrices = StoreUtil.calculateItemPrices(cart.items, {
			perItem: true,
		});
		itemsWithPrices.forEach(item => {
			items.push(convertCartItemToGA4Format(item));
		});
		const viewCartValue = parseFloat(
			String(_.get(cart, "prices.subtotal", "0"))
		);
		sendDataLayerEvent("view_cart", {
			currency: "USD",
			items,
			value: _.isFinite(viewCartValue) ? viewCartValue : 0,
			content_ids: items.map(item => item.item_id),
		});
	};

	// Before ignoring hits on same page, check for case where on
	// cart and cart didn't load in time for first vault update with
	// new page. Covers the first instance of cart page.
	if (
		GA4Flags.waitingForCartOnCartPage &&
		OpsPage.isPageCart(page) &&
		_.get(cart, "cartID")
	) {
		sendGA4ViewCart();
		GA4Flags.waitingForCartOnCartPage = false;
	}
	// Track checkout (GA4 `begin_checkout`)
	//
	// Intended behavior:
	// - Fires once per checkout "session" the first time `page` is `office.checkout`
	//   with `step` anything other than `cart` or `confirm` (e.g. account, shipping,
	//   billing, paypalinfo, review). It does NOT fire on `/checkout/cart` — only
	//   after the shopper advances (Continue / step change).
	// - Resets when `page.name` leaves `office.checkout` entirely (e.g. back to
	//   bookstore), so a later return to checkout can fire again.
	// - If the shopper already advanced once (`GA4Flags.checkoutAlreadyBegun`) and
	//   only moves between non-cart steps or returns to `cart`, it will not fire
	//   again until they leave checkout (flag reset above).
	// - Requires `Analytics.start` to have run (`setupVaultUpdateAnalytics` in
	//   startReact.js): e.g. not for school-student flows where analytics is stopped.
	if (GA4Flags.checkoutAlreadyBegun && page.name !== "office.checkout") {
		// When checkout is left
		GA4Flags.checkoutAlreadyBegun = false;
		// Reset billing and shipping info when leaving checkout
		GA4Flags.shippingInfoAlreadyAdded = false;
		GA4Flags.paymentInfoAlreadyAdded = false;
	} else if (
		!GA4Flags.checkoutAlreadyBegun &&
		page.name === "office.checkout" &&
		cart &&
		page.step !== "cart" &&
		page.step !== "confirm"
	) {
		const beginCheckoutValue = parseFloat(
			String(_.get(cart, "prices.subtotal", "0"))
		);
		sendDataLayerEvent("begin_checkout", {
			items: convertCartItemsToGA4Format(cart.items),
			value: _.isFinite(beginCheckoutValue) ? beginCheckoutValue : 0,
			currency: "USD",
			coupon: _.get(cart, "coupon.code", null),
		});
		GA4Flags.checkoutAlreadyBegun = true;
	}
	// Track payment and shipping info added
	if (
		GA4Flags.checkoutAlreadyBegun &&
		!GA4Flags.shippingInfoAlreadyAdded &&
		page.step === "billing"
	) {
		sendDataLayerEvent("add_shipping_info", {
			items: convertCartItemsToGA4Format(cart.items),
			shipping_tier: _.get(cart, "form.data.shipping.method", null),
			currency: "USD",
			value: parseFloat(_.get(cart, "prices.shipping")),
			coupon: _.get(cart, "coupon.code", null),
		});
		GA4Flags.shippingInfoAlreadyAdded = true;
	}
	if (GA4Flags.checkoutAlreadyBegun && page.step === "review") {
		if (
			StoreUtil.hasShippableItems(cart.items) &&
			!GA4Flags.shippingInfoAlreadyAdded
		) {
			sendDataLayerEvent("add_shipping_info", {
				items: convertCartItemsToGA4Format(cart.items),
				shipping_tier: _.get(cart, "form.data.shipping.method", null),
				currency: "USD",
				value: parseFloat(_.get(cart, "prices.shipping")),
				coupon: _.get(cart, "coupon.code", null),
			});
			GA4Flags.shippingInfoAlreadyAdded = true;
		}
		// Included here since we don't always know what page is after billing section
		if (!GA4Flags.paymentInfoAlreadyAdded) {
			sendDataLayerEvent("add_payment_info", {
				items: convertCartItemsToGA4Format(cart.items),
				payment_type: cart.onPaypalFlow ? "Paypal" : "Credit Card",
				coupon: _.get(cart, "coupon.code", null),
			});
			GA4Flags.paymentInfoAlreadyAdded = true;
		}
	}

	if (lastURL === url) {
		return;
	}
	lastURL = url;
	// Google Analytics page_view for Universal Analytics default page view events
	if (global.ga) {
		global.ga("set", "page", forceURL || url);
		global.ga("send", "pageview");
	}
	if (!suppressedFirstPageEvents) {
		// avoids double-firing tracking events since loading tracking snippets
		// already fires these events
		suppressedFirstPageEvents = true;
	} else {
		// DataLayer (used to trigger the GTM->GA4 pipeline)
		if (global.dataLayer) {
			global.dataLayer.push({
				event: "virtualPageView",
				eventModel: {
					page_location:
						document.location.protocol +
						"//" +
						document.location.hostname +
						lastURL +
						document.location.search,
					page_title: document.title,
				},
			});
		}
	}
	// GA4 Page events
	// Cart page (for once cart is loaded)
	if (OpsPage.isPageCart(page)) {
		if (_.get(cart, "cartID")) {
			sendGA4ViewCart();
		} else {
			GA4Flags.waitingForCartOnCartPage = true;
		}
	}
});

const sendEvent = conversionWrapper((category, action, label) => {
	// TODO: When we stop using Universal Analytics, make sure to remove
	// instances of global.ga. Replace any event calls with sendDataLayerEvent
	// below.
	const fields = {hitType: "event"};
	if (category) fields.eventCategory = category;
	if (action) fields.eventAction = action;
	if (label) fields.eventLabel = label;
	if (global.ga) {
		global.ga("send", fields);
	}
	sendDataLayerEvent("universalAnalyticsEvent", fields);
});

const sendDataLayerEvent = conversionWrapper((eventName, data = {}) => {
	if (!isProduction && !_.isEmpty(data)) {
		// eslint-disable-next-line no-console
		console.log(
			"Sending data layer event. Uncomment code below for more info."
		);
		// // eslint-disable-next-line no-console
		// console.log(
		// 	`%cSending data layer event "${eventName}"%c with data ${JSON.stringify(
		// 		data,
		// 		null,
		// 		2
		// 	)}`,
		// 	"color:black;background-color:lightblue;",
		// 	"color:default;background-color:default;"
		// );
	}

	if (global.pushToDataLayer) {
		global.pushToDataLayer({event: eventName, eventModel: data});
	}
});

const convertCartItemToGA4Format = cartItem => {
	const ga4Item = {
		item_id: cartItem.sku,
		item_category: StoreUtil.getItemType(cartItem),
		quantity: cartItem.quantity,
	};
	if (cartItem.price) {
		// Note: cartItem.price here is instance of Big (from big.js)
		ga4Item.price = parseFloat(cartItem.price.toFixed(2));
		ga4Item.currency = "USD";
	}
	return ga4Item;
};

const convertCartItemsToGA4Format = cartItems => {
	return _.map(cartItems, ci => convertCartItemToGA4Format(ci));
};

const getClientDeviceID = function(cookieUtil) {
	let clientDeviceID = cookieUtil.get("ba_clientDeviceID");
	if (!clientDeviceID) {
		clientDeviceID = uuidv4();
		cookieUtil.set("ba_clientDeviceID", clientDeviceID, COOKIE_DURATION);
	}
	return clientDeviceID;
};

const getHotjarSuppressionAttributes = () => {
	return {
		"data-hj-suppress": "",
		"data-hj-masked": "",
	};
};

module.exports = {
	start,
	stop,
	isActive,
	sendEmailSignup,
	sendOrder,
	sendPageEvents,
	sendEvent,
	sendDataLayerEvent,
	getHotjarSuppressionAttributes,
	convertCartItemsToGA4Format,
	getClientDeviceID,
};
